import { useTranslation } from "react-i18next";
import useClientConfig from "../../common/hooks/clientConfigHook";
import useCredentials from "../../common/hooks/credentialsHook";
import { useResizeContext } from "../../common/components/ResizeablePanel";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ReactNodeDialog } from "../../common/utils/dialogUtlities";
import DefaultButton from "../../common/components/DefaultButton";
import ChartConfig, { validChartConfig } from "../../common/components/chart/ChartConfig";
import DynamicPanelConfig, { validDynamicPanelConfig } from "../../common/components/chart/DynamicPanelConfig";
import { Button, Col, Row } from "react-bootstrap";
import DataTable from "../../common/components/DataTable";
import DynamicChart from "../../common/components/chart/DynamicChart";
import DynamicPanel from "../../common/components/chart/DynamicPanel";
import { Responsive as ResponsiveGridLayout } from "react-grid-layout";
import Icon from "../../common/components/Icon";
import { deepClone } from "../../common/utils/objectUtilities";
import { useUpsertSetting } from "../../common/hooks/settingsHooks";

const StatsDesktop = ({ data, fields, personalLayoutKey, defaultLayoutKey, headerControls }) => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const credentials = useCredentials();
    const upsertSetting = useUpsertSetting();
    const permitEdit = credentials.access.hasFull('administrator');
    const { width } = useResizeContext();
    
    const [allowEdit, setAllowEdit] = useState(false);
    const [selectedChart, setSelectedChart] = useState();
    const [selectedPanel, setSelectedPanel] = useState();
    const [loadSave, setLoadSave] = useState(false);
    
    const [panels, setPanels] = useState([]);

    useEffect(() => {
        if (panels.length === 0) {
            if (clientConfig[personalLayoutKey]?.some(l => l.default)) {
                setPanels(clientConfig[personalLayoutKey].find(l => l.default).panels);
            } else if (clientConfig[defaultLayoutKey]?.length > 0) {
                const layout = clientConfig[defaultLayoutKey].find(l => l.default) || clientConfig[defaultLayoutKey][0];
                setPanels(layout.panels); 
            }
        }
    }, [panels, clientConfig, personalLayoutKey, defaultLayoutKey])

    const handleLayoutChange = useCallback(layout => {
        setPanels(panels => panels.map(p => {
            let update = { ...p };
            let l = layout.find(l => l.i == p.id);
            if (l) {
                update.panel = { ...update.panel, x: l.x, y: l.y, w: l.w, h: l.h };
            }
            return update;
        }));
    }, []);

    const handleSaveLayout = useCallback((username, layouts) => {
        upsertSetting(username, username ? personalLayoutKey : defaultLayoutKey, layouts);
    }, [upsertSetting, personalLayoutKey, defaultLayoutKey]);

    const personalLoadSaveColumns = useMemo(() => [
        { type: "text", field: "name", title: t('name'), editable: false, readonly: true, placeholder: t('name') },
        { type: "checkbox", field: "default", title: t('default'), editable: true, readonly: false },
        { type: "dynamic", title: "", key: 'load', editable: false, readonly: true, def: (value, disabled, hideLabel, onChange) => <DefaultButton kind="load" onClick={() => { setPanels(value.panels); setLoadSave(false); }}></DefaultButton> },
        { type: "dynamic", title: "", key: 'save', editable: false, readonly: true, def: (value, disabled, hideLabel, onChange) => value.username || permitEdit ? <DefaultButton kind="save" onClick={() => handleSaveLayout(value.username, clientConfig[personalLayoutKey].map(l => l.name === value.name ? { ...l, panels } : l))}></DefaultButton> : null }
    ], [t, permitEdit, handleSaveLayout, setPanels, panels, clientConfig, personalLayoutKey]);

    const defaultLoadSaveColumns = useMemo(() => [
        { type: "text", field: "name", title: t('name'), editable: false, readonly: true, placeholder: t('name') },
        { type: "checkbox", field: "default", title: t('default'), editable: true, readonly: false },
        { type: "dynamic", title: "", key: 'load', editable: false, readonly: true, def: (value, disabled, hideLabel, onChange) => <DefaultButton kind="load" onClick={() => { setPanels(value.panels); setLoadSave(false); }}></DefaultButton> },
        { type: "dynamic", title: "", key: 'save', editable: false, readonly: true, def: (value, disabled, hideLabel, onChange) => value.username || permitEdit ? <DefaultButton kind="save" onClick={() => handleSaveLayout(value.username, clientConfig[defaultLayoutKey].map(l => l.name === value.name ? { ...l, panels } : l))}></DefaultButton> : null }
    ], [t, permitEdit, handleSaveLayout, setPanels, panels, clientConfig, defaultLayoutKey]);

    const loadSaveFooterColumns = useMemo(() => [
        { type: "text", field: "name", title: t('name'), editable: true, placeholder: t('name') },
        { type: "checkbox", field: "default", title: t('default'), editable: true, readonly: false }
    ], [t]);

    return <>
        <ReactNodeDialog title={t('chart')} size="xlg" showModal={!!selectedChart} onSave={() => {
            if (selectedChart?.id) {
                setPanels(panels => panels.map(c => c.id === selectedChart.id ? selectedChart : c));
            } else {
                setPanels(panels => [...panels, { ...selectedChart, panel: { x: 0, y: 0, w: 1, h: 1, ...selectedChart?.panel }, kind: 'chart', id: panels.reduce((max, cur) => cur.id > max ? cur.id : max, 0) + 1 }]);
            }
            setSelectedChart();
        }}
        onClose={() => setSelectedChart()} invalid={!validChartConfig(selectedChart)}
        footerControls={<DefaultButton kind="delete" onClick={() => { setPanels(panels => panels.filter(p => p.id !== selectedChart.id)); setSelectedChart(); }}></DefaultButton>}>
            <ChartConfig fields={fields} chart={selectedChart} onChange={setSelectedChart}></ChartConfig>
        </ReactNodeDialog>
        <ReactNodeDialog title={t('panel')} size="xlg" showModal={!!selectedPanel} onSave={() => {
            if (selectedPanel?.id) {
                setPanels(panels => panels.map(p => p.id === selectedPanel.id ? selectedPanel : p));
            } else {
                setPanels(panels => [...panels, { ...selectedPanel, panel: { x: 0, y: 0, w: 1, h: 1, ...selectedPanel?.panel }, kind: 'panel', id: panels.reduce((max, cur) => cur.id > max ? cur.id : max, 0) + 1 }]);
            }
            setSelectedPanel();
        }} onClose={() => setSelectedPanel()} invalid={!validDynamicPanelConfig(selectedPanel)}
        footerControls={<DefaultButton kind="delete" onClick={() => { setPanels(panels => panels.filter(p => p.id !== selectedPanel.id)); setSelectedPanel(); }}></DefaultButton>}>
            <DynamicPanelConfig config={selectedPanel} fields={fields} onChange={setSelectedPanel}></DynamicPanelConfig>
        </ReactNodeDialog>
        <ReactNodeDialog title={t('load') + ' / ' + t('save')} size="lg" showModal={loadSave} onClose={() => { setLoadSave(false)}}>
            <Row>
                <Col>
                    <h4>{t('defaultLayouts')}</h4>
                </Col>
            </Row>
            <Row>
                <Col>
                    <DataTable columns={defaultLoadSaveColumns} value={clientConfig[defaultLayoutKey] ?? []} footerColumns={allowEdit ? loadSaveFooterColumns : null}  onAdd={entry => {
                        const currentLayouts = [...(clientConfig[defaultLayoutKey] ?? [])];
                        if (entry.default) {
                            currentLayouts.forEach(l => l.default = false);
                        }
                        currentLayouts.push({ ...entry, panels });
                        handleSaveLayout(null, currentLayouts);
                    }} onUpdate={(entry, field, value) => {
                        if (value) {
                            handleSaveLayout(null, clientConfig[defaultLayoutKey].map(l => l.name === entry.name ? { ...l, default: true } : { ...l, default: false }));
                        } else {
                            const layouts = clientConfig[defaultLayoutKey].map(l => l.name === entry.name ? { ...l, default: false } : l);
                            if (layouts.every(l => !l.default)) {
                                layouts[0].default = true;
                            }
                            handleSaveLayout(null, layouts);
                        }
                    }} onRemove={entry => {
                        const layouts = clientConfig[defaultLayoutKey].filter(l => l.name !== entry.name);
                        if (layouts.every(l => !l.default)) {
                            layouts[0].default = true;
                        }
                        handleSaveLayout(null, layouts);
                    }} defaultSort={['name']} disabled={!allowEdit}></DataTable>
                </Col>
            </Row>
            <Row>
                <Col>
                    <h4>{t('personalLayouts')}</h4>
                </Col>
            </Row>
            <Row>
                <Col>
                <DataTable columns={personalLoadSaveColumns} footerColumns={loadSaveFooterColumns} value={clientConfig[personalLayoutKey] ?? []} onAdd={entry => {
                        const currentLayouts = [...(clientConfig[personalLayoutKey] ?? [])];
                        if (entry.default) {
                            currentLayouts.forEach(l => l.default = false);
                        }
                        currentLayouts.push({ ...entry, panels });
                        handleSaveLayout(credentials.username, currentLayouts);
                    }} onUpdate={(entry, field, value) => {
                        if (value) {
                            handleSaveLayout(credentials.username, clientConfig[personalLayoutKey].map(l => l.name === entry.name ? { ...l, default: true } : { ...l, default: false }));
                        } else {
                            handleSaveLayout(credentials.username, clientConfig[personalLayoutKey].map(l => l.name === entry.name ? { ...l, default: false } : l));
                        }
                    }}
                    onRemove={entry => {
                        handleSaveLayout(credentials.username, clientConfig[personalLayoutKey].filter(l => l.name !== entry.name));
                    }} defaultSort={['name']}></DataTable>
                </Col>
            </Row>
        </ReactNodeDialog>

        <div className="d-flex h-100 w-100" style={{ flexDirection: 'column', }}>
            <Row className="w-100" style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 4 }}>
                {headerControls ? <Col>{headerControls}</Col> : <Col></Col>}
                <Col xs="auto">
                    { allowEdit ? <DefaultButton className="mx-2" kind="lock" label={t('lock')} onClick={() => setAllowEdit(false)} ></DefaultButton> 
                                : <DefaultButton className="mx-2" kind="unlock" label={t('unlock')}  onClick={() => setAllowEdit(true)}></DefaultButton>
                    }
                </Col>
                { allowEdit ? <><Col xs="auto">
                    <DefaultButton className="mx-2" kind="add" label={t('addPanel')} onClick={() => setSelectedPanel({})}></DefaultButton>
                </Col>
                <Col xs="auto">
                    <DefaultButton className="mx-2" kind="add" label={t('addChart')} onClick={() => setSelectedChart({})}></DefaultButton>
                </Col>
                <Col xs="auto">
                    <Button variant="primary" className="mx-2" onClick={() => setLoadSave(true)}><Icon intent="save"></Icon> {t('load')} / {t('save')}...</Button>
                </Col>
                </> : null }
            </Row>
            <ResponsiveGridLayout
                className="h-100 w-100"
                width={width}
                style={{ flex: 1 }}
                breakpoints={{ xs: 0, sm: 576, md: 768, lg: 992, xl: 1200, xxl: 1500 }}
                cols={{ xs: 1, sm: 2, md: 6, lg: 8, xl: 10, xxl: 12 }}
                rowHeight={96}
                onDragStop={handleLayoutChange}
                onResizeStop={handleLayoutChange}
                containerPadding={[ 30, 10 ]}
            >
                {panels.map(panel => <div key={panel.id} data-grid={{ x: 0, y: 0, ...panel.panel, static: !allowEdit }}>
                    <div className="p-1" style={{ backgroundColor: '#f0f0f0' }}>
                        <Row>
                            <Col style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                                {panel.title}
                            </Col>
                            <Col xs="auto">
                                { allowEdit ? <DefaultButton className="m-1" size="sm" kind="settings" variant="outline-dark" hideLabel={true} onMouseDown={e => { e.preventDefault(); e.stopPropagation(); }} onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    (panel.kind === 'chart' ? setSelectedChart : setSelectedPanel)(deepClone(panel));
                                }}></DefaultButton> : null }
                            </Col>
                        </Row>
                    </div>
                    <div style={{ height: 'calc(100% - 42px)', width: 'calc(100% - 2px)' }}>
                        {panel.kind === 'chart' ? <DynamicChart chart={panel} data={data}></DynamicChart> : null}
                        {panel.kind === 'panel' ? <DynamicPanel panel={panel} data={data}></DynamicPanel> : null}
                    </div>
                </div>)
                }

            </ResponsiveGridLayout>
        </div>
    </>
}

export default StatsDesktop;